// src/components/albums/EditAlbumModal.js

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getFirestore, doc, updateDoc } from 'firebase/firestore';
import { useProfile } from '../../context/ProfileContext';
import { uploadFile } from '../../services/fileUpload';
import toast from 'react-hot-toast';

// 🌟 UPGRADED: Destructuring isDarkMode to map context changes smoothly
const EditAlbumModal = ({ isOpen, onClose, album, onAlbumUpdated, isDarkMode }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [artworkFile, setArtworkFile] = useState(null);
  const [artworkPreview, setArtworkPreview] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { activeProfile } = useProfile();

  useEffect(() => {
    if (album) {
      setName(album.name || '');
      setDescription(album.description || '');
      setArtworkPreview(album.artworkUrl);
    }
  }, [album]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setArtworkFile(file);
      setArtworkPreview(URL.createObjectURL(file));
    }
  };

  const handleSave = async () => {
    if (!album?.id) return;
    if (!name.trim()) return toast.error('Album name cannot be empty.');
    
    setIsLoading(true);
    const db = getFirestore();
    const albumRef = doc(db, 'albums', album.id);
    const updateData = { name, description };

    try {
      if (artworkFile) {
        const filePath = `album_artwork/${activeProfile.id}/${Date.now()}_${artworkFile.name}`;
        const { fileURL } = await uploadFile(artworkFile, filePath);
        updateData.artworkUrl = fileURL;
      }
      
      await updateDoc(albumRef, updateData);
      toast.success("Album updated!");
      onAlbumUpdated();
      onClose();
    } catch (error) {
      console.error("Error updating album:", error);
      toast.error("Failed to update album.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div 
          className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 backdrop-blur-sm" 
          initial={{ opacity: 0 }} 
          animate={{ opacity: 1 }} 
          exit={{ opacity: 0 }} 
          onClick={onClose}
        >
          {/* 🌟 UPGRADED: Unified Light/Dark Modal Base Chassis Layout */}
          <motion.div 
            className={`rounded-2xl shadow-2xl p-6 w-full max-w-md border transition-all duration-300 ${
              isDarkMode 
                ? "bg-zinc-900 border-zinc-800 text-white shadow-black/60" 
                : "bg-white border-slate-100 text-slate-900 shadow-slate-200"
            }`}
            initial={{ scale: 0.9, y: 10 }} 
            animate={{ scale: 1, y: 0 }} 
            exit={{ scale: 0.9, opacity: 0, y: 10 }}
            transition={{ type: "spring", duration: 0.4 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-sm font-black uppercase tracking-wider mb-4 italic">
              Edit Album Details
            </h2>
            
            <div className="space-y-4">
              {/* 🌟 UPGRADED: Dynamic Album Title input box */}
              <input 
                type="text" 
                value={name} 
                onChange={(e) => setName(e.target.value)} 
                placeholder="Album Name" 
                className={`w-full p-3 text-xs font-semibold rounded-xl border transition-all focus:outline-none focus:border-brand-pink/50 ${
                  isDarkMode 
                    ? "bg-zinc-950 border-zinc-800 text-white placeholder-zinc-600 shadow-[inset_0_1px_2px_rgba(0,0,0,0.4)]" 
                    : "bg-white border-slate-200 text-slate-900 placeholder-slate-400"
                }`}
              />
              
              {/* 🌟 UPGRADED: Dynamic Description panel texture context area */}
              <textarea 
                value={description} 
                onChange={(e) => setDescription(e.target.value)} 
                placeholder="Description" 
                rows="3" 
                className={`w-full p-3 text-xs font-semibold rounded-xl border transition-all focus:outline-none focus:border-brand-pink/50 resize-none ${
                  isDarkMode 
                    ? "bg-zinc-950 border-zinc-800 text-white placeholder-zinc-600 shadow-[inset_0_1px_2px_rgba(0,0,0,0.4)]" 
                    : "bg-white border-slate-200 text-slate-900 placeholder-slate-400"
                }`}
              />
              
              {/* 🌟 UPGRADED: Media collection block deck context section */}
              <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-zinc-950/40 border-zinc-800' : 'bg-slate-50 border-slate-200'}`}>
                <label className={`block text-[10px] font-black uppercase tracking-wider mb-1.5 ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
                  Change Artwork
                </label>
                <input 
                  type="file" 
                  accept="image/*" 
                  onChange={handleFileChange} 
                  className={`block w-full text-xs font-semibold cursor-pointer file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-[10px] file:font-black file:uppercase file:tracking-wider ${
                    isDarkMode 
                      ? 'text-zinc-500 file:bg-zinc-800 file:text-zinc-300 hover:file:bg-zinc-700' 
                      : 'text-slate-500 file:bg-slate-200 file:text-slate-700 hover:file:bg-slate-300'
                  }`} 
                />
                {artworkPreview && (
                  <div className="mt-3 flex items-center justify-start">
                    <img 
                      src={artworkPreview} 
                      alt="Artwork preview" 
                      className={`w-20 h-20 rounded-xl object-cover shadow-lg border ${isDarkMode ? 'border-zinc-800' : 'border-slate-200'}`} 
                    />
                  </div>
                )}
              </div>
            </div>
            
            {/* Modal actions panel layout trigger buttons deck */}
            <div className="flex justify-end space-x-3 mt-6">
              <button 
                onClick={onClose} 
                className={`px-4 py-2 text-[10px] font-black uppercase tracking-widest rounded-lg transition-colors focus:outline-none ${
                  isDarkMode 
                    ? "text-zinc-400 hover:text-white hover:bg-zinc-800" 
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                Cancel
              </button>
              
              <button 
                onClick={handleSave} 
                disabled={isLoading} 
                className={`px-5 py-2 text-[10px] font-black uppercase tracking-widest rounded-lg transition-all shadow-md active:scale-95 disabled:pointer-events-none ${
                  isLoading
                    ? "bg-zinc-700 text-zinc-500"
                    : isDarkMode
                      ? "bg-brand-pink text-white hover:brightness-110"
                      : "bg-pink-600 text-white hover:bg-pink-700"
                }`}
              >
                {isLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default EditAlbumModal;